import { useCallback, useRef, useState } from "react";

interface OptimisticPick<Version> {
    classIds: string[];
    /** Set once the save settled: the row's assignments at that moment. */
    settledVersion?: { value: Version };
}

export interface OptimisticClassIds {
    /** What the cell renders: the pending pick, else the row's classes. */
    classIds: string[];
    /** Shows `classIds` right away while the save runs. */
    begin: (classIds: string[]) => void;
    /** Ends the save; a successful pick stays until the row's assignments change. */
    settle: (outcome: { didSucceed: boolean }) => void;
}

/**
 * Optimistic class ids for a transaction cell. The saved transaction is
 * written to the query cache before the save resolves, but React Query hands
 * it to the table a tick later, so dropping the pick when the save settles
 * would flash the previous classes for a frame. The pick is held until the
 * row's own `rowAssignments` reference changes instead; a failed save drops
 * it at once.
 */
export const useOptimisticClassIds = <Version>({
    serverClassIds,
    rowAssignments,
}: {
    serverClassIds: string[];
    rowAssignments: Version;
}): OptimisticClassIds => {
    const [pick, setPick] = useState<OptimisticPick<Version> | null>(null);
    const rowAssignmentsRef = useRef(rowAssignments);
    rowAssignmentsRef.current = rowAssignments;

    const begin = useCallback((classIds: string[]) => {
        setPick({ classIds });
    }, []);

    const settle = useCallback(({ didSucceed }: { didSucceed: boolean }) => {
        setPick((current) =>
            current !== null && didSucceed
                ? {
                      ...current,
                      settledVersion: { value: rowAssignmentsRef.current },
                  }
                : null,
        );
    }, []);

    const isPickShown =
        pick !== null &&
        (pick.settledVersion === undefined ||
            pick.settledVersion.value === rowAssignments);

    return {
        classIds: isPickShown ? pick.classIds : serverClassIds,
        begin,
        settle,
    };
};
